import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import AccountsHeader from "./header";
import CountryCodeDropdown from "../../../components/CountryDropDown/CountryDropDown";
import { updateUserProfile } from "../../../store/slices/authSlice";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { FaImage } from "react-icons/fa";
import CropperModalAdmin from "../../../components/models/CropperModalAdmin";

const validationSchema = Yup.object({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  phone: Yup.string()
    .required("Phone number is required")
    .test("isValidPhone", "Invalid phone number", (value) => {
      if (!value) return false;
      const formattedValue = value.startsWith("+") ? value : `+${value}`;
      const parsedNumber = parsePhoneNumberFromString(formattedValue);
      return parsedNumber ? parsedNumber.isValid() : false;
    }),
});

const Contact = ({ targetGroupAdmin = false, accountBasePath = "/admin/dashboard/account", showUpdatePassword = true }) => {
  const { user, otherUser } = useSelector((state) => state.auth);
  const profileUser = targetGroupAdmin ? (otherUser || user) : user;
  const [isSaving, setIsSaving] = useState(false);
  const [croppedImage, setCroppedImage] = useState(profileUser?.attributes?.avatar);
  const [showModal, setShowModal] = useState(false);
  const [image, setImage] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isImageDeleted, setIsImageDeleted] = useState(false);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      firstName: profileUser?.attributes?.firstName || "",
      lastName: profileUser?.attributes?.lastName || "",
      phone: profileUser?.attributes?.phone || "",
      avatar: profileUser?.attributes?.avatar || "",
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      setIsSaving(true);
      try {
        // If image is deleted, set avatar to empty string
        const updatedValues = {
          ...values,
          avatar: isImageDeleted ? "" : values.avatar
        };
        await dispatch(updateUserProfile({ values: updatedValues, targetGroupAdmin })).unwrap();
        setIsImageDeleted(false);
      } catch (error) {
        console.error("Profile update failed:", error);
      } finally {
        setIsSaving(false);
      }
    },
  });

  // Update croppedImage when user profile photo changes
  useEffect(() => {
    const avatar = profileUser?.attributes?.avatar;
    if (avatar) {
      setCroppedImage(avatar);
      setIsImageDeleted(false);
    } else {
      setCroppedImage(null);
      setIsImageDeleted(false);
    }
  }, [profileUser?.attributes?.avatar]);

  const handleCountryChange = (countryCode) => {
    formik.setFieldValue("phone", `${countryCode} `);
  };

  const handleCrop = (croppedImg) => {
    setCroppedImage(croppedImg);
    formik.setFieldValue("avatar", croppedImg);
    setIsImageDeleted(false);
    setShowModal(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result);
        setShowModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDeleteImage = (e) => {
    e.stopPropagation(); // Prevent triggering the file input
    setIsImageDeleted(true);
  };

  const handleUndoDeleteImage = (e) => {
    e.stopPropagation(); // Prevent triggering the file input
    setIsImageDeleted(false);
  };

  return (
    <div className="section_main_div">
      <h1 className="section_heading">Account</h1>
      <AccountsHeader basePath={accountBasePath} showUpdatePassword={showUpdatePassword} />

      <form className="form_div" onSubmit={formik.handleSubmit}>
        <div className="form-card">
          <div className="form-elements">
          <div className="row">
              <div className="d-flex justify-content-between align-items-center">
               
                <div className="position-relative">
                  <div className="d-flex align-items-center mb-3">
                    {croppedImage && !isImageDeleted ? (
                      <>
                        <div
                          style={{ position: 'relative' }}
                          onMouseEnter={() => setIsHovered(true)}
                          onMouseLeave={() => setIsHovered(false)}
                        >
                          <img
                            src={croppedImage}
                            alt="Profile Photo"
                            className="img-fluid"
                            style={{
                              width: "150px",
                              height: "150px",
                              objectFit: "contain",
                              border: "2px solid #ddd",
                              borderRadius: "8px",
                              backgroundColor: "#f8f9fa",
                              transition: "opacity 0.3s ease",
                              opacity: isHovered ? 0.7 : 1,
                              cursor: "pointer"
                            }}
                            onClick={handleImageClick}
                          />
                          
                          {/* Upload icon overlay on hover */}
                          {isHovered && (
                            <div
                              className="position-absolute d-flex align-items-center justify-content-center"
                              style={{
                                top: 0,
                                left: 0,
                                width: "150px",
                                height: "150px",
                                backgroundColor: "rgba(0,0,0,0.5)",
                                borderRadius: "8px",
                                transition: "opacity 0.3s ease",
                                pointerEvents: "none"
                              }}
                            >
                              <FaImage size={32} color="white" />
                            </div>
                          )}
                          
                          <label
                            htmlFor="profileUpload"
                            className="position-absolute"
                            style={{
                              top: 0,
                              left: 0,
                              width: "150px",
                              height: "150px",
                              cursor: "pointer",
                              zIndex: 5
                            }}
                          />
                        </div>
                        
                        {/* Delete button */}
                        <button
                          type="button"
                          onClick={handleDeleteImage}
                          style={{ border: "0px", outline: "0px", cursor: "pointer", background: "none", marginLeft: "10px", zIndex: 10, position: "relative" }}
                        >
                          Delete
                        </button>
                      </>
                    ) : croppedImage && isImageDeleted ? (
                      <>
                        <div style={{ position: 'relative' }}>
                          <img
                            src={croppedImage}
                            alt="Profile Photo"
                            className="img-fluid"
                            style={{
                              width: "150px",
                              height: "150px",
                              objectFit: "contain",
                              border: "2px solid #ddd",
                              borderRadius: "8px",
                              backgroundColor: "#f8f9fa"
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "150px",
                              height: "150px",
                              background: "rgba(0, 0, 0, 0.65)",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              zIndex: 5,
                              borderRadius: "8px",
                            }}
                          >
                            <button
                              type="button"
                              onClick={handleUndoDeleteImage}
                              className="simpleBtn"
                            >
                              <span>UNDO DELETE</span>
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div
                        className="position-relative"
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        style={{ cursor: "pointer" }}
                      >
                        <div
                          className="d-flex flex-column align-items-center justify-content-center"
                          style={{
                            width: "150px",
                            height: "150px",
                            backgroundColor: "#f8f9fa",
                            border: "2px dashed #ddd",
                            borderRadius: "8px",
                            transition: "all 0.3s ease",
                            opacity: isHovered ? 0.7 : 1
                          }}
                          onClick={handleImageClick}
                        >
                          <FaImage size={24} className="mb-2" />
                          <small className="text-muted">Profile Photo</small>
                        </div>
                        
                        <label
                          htmlFor="profileUpload"
                          className="position-absolute"
                          style={{
                            top: 0,
                            left: 0,
                            width: "150px",
                            height: "150px",
                            cursor: "pointer",
                            zIndex: 5
                          }}
                        />
                      </div>
                    )}
                    
                    <input
                      type="file"
                      id="profileUpload"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="custom-form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="off"
                required
              />
              {formik.touched.firstName && formik.errors.firstName ? (
                <div className="error-text">{formik.errors.firstName}</div>
              ) : null}
              <hr
                className={
                  formik.touched.firstName && formik.errors.firstName
                    ? "error-hr-line"
                    : ""
                }
              />
            </div>
            <div className="custom-form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="off"
                required
              />
              {formik.touched.lastName && formik.errors.lastName ? (
                <div className="error-text">{formik.errors.lastName}</div>
              ) : null}
              <hr
                className={
                  formik.touched.lastName && formik.errors.lastName
                    ? "error-hr-line"
                    : ""
                }
              />
            </div>
            <div className="custom-form-group">
              <label htmlFor="email">
                Your email, contact us if you need to change it
              </label>
              <input
                type="text"
                value={profileUser?.id || ""}
                disabled={true}
              />
              <hr
                className={
                  formik.touched.email && formik.errors.email
                    ? "error-hr-line"
                    : ""
                }
              />
            </div>
            <div className="custom-form-group d-flex justify-space-between">
              <CountryCodeDropdown
                label={"Account Owner Phone"}
                phoneNumber={formik.values.phone}
                onPhoneNumberChange={handleCountryChange}
              />
              <hr
                className={
                  formik.touched.phone && formik.errors.phone
                    ? "error-hr-line"
                    : ""
                }
              />
              {formik.touched.phone && formik.errors.phone ? (
                <div className="error-text">{formik.errors.phone}</div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="button-div">
            <button type="submit" disabled={isSaving}>
              <span>{isSaving ? "Updating..." : "Update"}</span>
            </button>
          </div>
        </div>
      </form>
      <CropperModalAdmin
        show={showModal}
        onClose={() => setShowModal(false)}
        image={image}
        onCrop={handleCrop}
        heading="Profile Photo"
      />
    </div>
  );
};

export default Contact;
